import type { AirRateQuote } from '@aircargomcp/types'
import { type FormEvent, useState } from 'react'
import { type ApiError, quoteRate } from '../lib/api'
import { RateQuoteCard } from './RateQuoteCard'

type State =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'result'; data: AirRateQuote }
  | { kind: 'error'; error: ApiError }

function errorCopy(e: ApiError): { head: string; body: string } {
  switch (e.code) {
    case 'not_found':
      return {
        head: 'No rate card for that lane yet',
        body: `${e.message}. The sample dataset covers HKG → FRA. Try that pair.`,
      }
    case 'invalid_reference':
      return {
        head: 'Check the lane inputs',
        body: 'Origin and destination are 3-letter IATA airport codes, and weight must be a positive number.',
      }
    default:
      return {
        head: 'Rate service unavailable',
        body: e.message || 'Please try again in a moment.',
      }
  }
}

// A single IATA-code field.
function CodeField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder: string
}) {
  return (
    <div className="min-w-0 flex-1 px-4 py-2.5">
      <div className="mono text-[10px] uppercase tracking-wider text-faint">{label}</div>
      <input
        value={value}
        onChange={(e) =>
          onChange(
            e.target.value
              .toUpperCase()
              .replace(/[^A-Z]/g, '')
              .slice(0, 3),
          )
        }
        placeholder={placeholder}
        spellCheck={false}
        autoComplete="off"
        className="mono w-full bg-transparent text-[18px] font-bold uppercase tracking-wide text-ink outline-none placeholder:font-normal placeholder:tracking-normal placeholder:text-faint"
      />
    </div>
  )
}

export function RateForm() {
  const [origin, setOrigin] = useState('')
  const [destination, setDestination] = useState('')
  const [weight, setWeight] = useState('')
  const [volume, setVolume] = useState('')
  const [state, setState] = useState<State>({ kind: 'idle' })

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    const w = Number(weight)
    if (origin.length < 3 || destination.length < 3 || !(w > 0)) return
    setState({ kind: 'loading' })
    const vol = Number(volume)
    const res = await quoteRate({
      origin,
      destination,
      weight_kg: w,
      ...(volume && vol > 0 ? { volume_cm3: vol } : {}),
    })
    setState(res.ok ? { kind: 'result', data: res.data } : { kind: 'error', error: res.error })
  }

  const ready = origin.length === 3 && destination.length === 3 && Number(weight) > 0

  return (
    <div>
      <form onSubmit={onSubmit} className="card rounded-2xl p-1.5">
        <div className="flex flex-col sm:flex-row sm:items-stretch">
          <CodeField label="Origin" value={origin} onChange={setOrigin} placeholder="HKG" />
          <div className="hidden w-px self-stretch bg-border sm:block" />
          <CodeField
            label="Destination"
            value={destination}
            onChange={setDestination}
            placeholder="FRA"
          />
          <div className="hidden w-px self-stretch bg-border sm:block" />
          <div className="min-w-0 flex-1 px-4 py-2.5">
            <div className="mono text-[10px] uppercase tracking-wider text-faint">
              Gross weight (kg)
            </div>
            <input
              value={weight}
              onChange={(e) => setWeight(e.target.value.replace(/[^\d.]/g, ''))}
              placeholder="350"
              inputMode="decimal"
              className="mono w-full bg-transparent text-[18px] font-bold text-ink outline-none placeholder:font-normal placeholder:text-faint tnum"
            />
          </div>
          <div className="hidden w-px self-stretch bg-border sm:block" />
          <div className="min-w-0 flex-1 px-4 py-2.5">
            <div className="mono text-[10px] uppercase tracking-wider text-faint">
              Volume (cm³, optional)
            </div>
            <input
              value={volume}
              onChange={(e) => setVolume(e.target.value.replace(/[^\d.]/g, ''))}
              placeholder="3000000"
              inputMode="decimal"
              className="mono w-full bg-transparent text-[18px] font-bold text-ink outline-none placeholder:font-normal placeholder:text-faint tnum"
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={!ready || state.kind === 'loading'}
          className="btn-accent mt-1.5 w-full rounded-xl px-6 py-3 text-center font-semibold disabled:cursor-not-allowed disabled:opacity-45"
        >
          {state.kind === 'loading' ? 'Pricing…' : 'Get rate'}
        </button>
      </form>

      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted">
        <span className="mono uppercase tracking-wider text-faint">Try</span>
        <button
          type="button"
          onClick={() => {
            setOrigin('HKG')
            setDestination('FRA')
            setWeight('350')
          }}
          className="mono rounded-full border border-border bg-surface-0 px-2.5 py-1 text-muted transition-colors hover:border-signal hover:text-ink"
        >
          HKG → FRA · 350kg
        </button>
        <span className="text-faint">general cargo · sample card</span>
      </div>

      {state.kind === 'result' && (
        <div className="reveal mt-5">
          <RateQuoteCard q={state.data} />
        </div>
      )}
      {state.kind === 'error' && (
        <div className="reveal card mt-5 border-l-2 border-l-down p-5">
          <div className="font-semibold text-ink">{errorCopy(state.error).head}</div>
          <p className="mt-1 text-sm text-muted">{errorCopy(state.error).body}</p>
        </div>
      )}
    </div>
  )
}
